<script>
  // Sets a user's profile picture on their behalf through the same prepare →
  // presigned PUT → finalize pipeline the app's avatar picker uses
  // (AdminSite::UserAvatarsController → ProfileService). Requires manage_social;
  // callers hide their entry point without it and the server enforces it.
  //
  // Prepare answers with the `prepared_upload` prop (lib/presigned_upload.js); the
  // finalize is an Inertia visit that redirects back with a flash. `only` names the
  // page props that visit refreshes (flash always rides along); `onsaved()` runs
  // after a successful save.
  import { router } from '@inertiajs/svelte'
  import Modal from './modal.svelte'
  import PhotoDropField from './photo_drop_field.svelte'
  import UserAvatar from './user_avatar.svelte'
  import { Button } from './ui'
  import { requestPreparedUpload, uploadPrepared } from '../lib/presigned_upload.js'

  // Server cap (UploadService::MAX_IMAGE_BYTES).
  const MAX_AVATAR_BYTES = 20 * 1024 * 1024

  let { open = $bindable(false), user = null, only = [], onsaved, onclose } = $props()

  let file = $state(null)
  let saving = $state(false)
  let uploadProgress = $state(null)
  let error = $state(null)

  const currentUrl = $derived(user?.icon_image_url ?? null)

  // The modal stays mounted while closed, so drop the picked file on close.
  $effect(() => {
    if (open) return
    file = null
    error = null
  })


  const finalize = (mediaUploadId) =>
    new Promise((resolve, reject) => {
      router.patch(
        `/users/${user.id}/avatar`,
        { media_upload_id: mediaUploadId },
        {
          only: [...only, 'flash'],
          preserveState: true,
          preserveScroll: true,
          onSuccess: () => resolve(),
          onError: (errors) => {
            const e = new Error(errors.avatar || 'The server rejected the request.')
            e.code = errors.avatar_code
            reject(e)
          },
          onNetworkError: () => reject(new Error('Network error — check your connection and retry.'))
        }
      )
    })

  const save = async () => {
    if (!file) {
      error = { title: 'Pick a photo first.' }
      return
    }
    error = null
    saving = true
    uploadProgress = 0
    try {
      const mediaUploadId = await uploadPrepared(() => requestPreparedUpload(`/users/${user.id}/avatar/prepare`), { avatar: file }, {
        onProgress: (fraction) => (uploadProgress = fraction)
      })

      await finalize(mediaUploadId)

      saving = false
      open = false
      onsaved?.()
    } catch (e) {
      error = {
        title: 'Profile picture not saved',
        text: e.code === 'ExpiredMediaUploadError' ? 'The upload expired before it could be saved — click Save to retry.' : e.message
      }
    } finally {
      saving = false
      uploadProgress = null
    }
  }
</script>

<!-- Closing mid-save would orphan the prepared upload, so the modal is locked while saving. -->
<Modal bind:open title={currentUrl ? 'Change profile picture' : 'Upload profile picture'} dismissible={!saving} {onclose}>
  <p class="text-sm text-base-content/60 mb-3">
    Shown everywhere {user?.name || 'this user'} appears in the app, as if they had uploaded it themselves.
  </p>

  <div class="flex items-center gap-3">
    <UserAvatar url={currentUrl} name={user?.name} size="lg" />
    <span class="text-sm">{user?.name}</span>
  </div>

  <PhotoDropField
    bind:file
    label="New photo"
    maxBytes={MAX_AVATAR_BYTES}
    disabled={saving}
    ontoolarge={() => (error = { title: 'Photo too large', text: 'Photos must be 20 MB or smaller.' })}
    onrejected={() => (error = { title: 'Unsupported file', text: 'Pick a single JPEG or PNG.' })}
  />
  <p class="text-xs text-base-content/60">JPEG or PNG, up to 20 MB. Replaces the current picture immediately.</p>

  {#if uploadProgress !== null}
    <div class="mt-4">
      <progress class="progress progress-primary w-full" value={Math.round(uploadProgress * 100)} max="100"></progress>
      <p class="text-xs text-base-content/60">Uploading photo… {Math.round(uploadProgress * 100)}%</p>
    </div>
  {/if}

  {#if error}
    <div class="alert alert-error alert-soft mt-4 text-sm" role="alert">
      <div>
        <div class="font-semibold">{error.title}</div>
        {#if error.text}<div class="whitespace-pre-line">{error.text}</div>{/if}
      </div>
    </div>
  {/if}

  <div class="flex justify-end gap-2 mt-4">
    <Button variant="ghost" disabled={saving} onclick={() => { open = false; onclose?.() }}>Cancel</Button>
    <Button variant="primary" loading={saving} disabled={!file || saving} onclick={save}>Save</Button>
  </div>
</Modal>
